import { useToolOwnership } from "./call/shared/toolOwnership"
import { useCallback, useEffect, useRef, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import { MessageSquare, RefreshCw, Send } from "lucide-react"

import { Button } from "components/ui/button"
import { Input } from "components/ui/input"
import { Textarea } from "components/ui/textarea"
import { meetings } from "services/api/meetingsClient"
import type {
  MeetingQuestionSubmission,
  MeetingQuestionSubmissionStatus,
  MeetingQuestionsPanelRole,
} from "services/api/meetingsClient"
import { useMeetingPanelEvents } from "./useMeetingPanelEvents"

const questionStatusSortOrder: Record<MeetingQuestionSubmissionStatus, number> = {
  submitted: 0,
  reviewed: 1,
  answered: 2,
  archived: 3,
}
const PANEL_POLL_INTERVAL_MS = 90000
const DEFAULT_THROTTLE_BACKOFF_MS = 5 * 60 * 1000
const TRANSIENT_ERROR_BACKOFF_MS = 60 * 1000

const sortQuestionSubmissions = (submissions: MeetingQuestionSubmission[]) =>
  [...submissions].sort((left, right) => {
    const statusDelta = questionStatusSortOrder[left.status] - questionStatusSortOrder[right.status]
    if (statusDelta !== 0) return statusDelta
    return new Date(right.created_at || 0).getTime() - new Date(left.created_at || 0).getTime()
  })

const formatQuestionSubmittedAt = (iso?: string | null) => {
  if (!iso) return ""
  const submittedAt = new Date(iso).getTime()
  if (Number.isNaN(submittedAt)) return ""
  const daysAgo = Math.floor((Date.now() - submittedAt) / (24 * 60 * 60 * 1000))
  if (daysAgo <= 0) return "Today"
  if (daysAgo === 1) return "Yesterday"
  if (daysAgo < 7) return `${daysAgo} days ago`
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

const getQuestionStatusSelectClass = (status: MeetingQuestionSubmissionStatus) => {
  if (status === "answered") return "border-emerald-400/35 bg-[#13271f] text-emerald-100"
  if (status === "archived") return "border-white/15 bg-[#1d2430] text-slate-200"
  if (status === "reviewed") return "border-sky-400/35 bg-[#11253a] text-sky-100"
  return "border-amber-400/35 bg-[#2f2415] text-amber-100"
}

const getThrottleBackoffMs = (err: any) => {
  const retryAfterHeader = err?.response?.headers?.["retry-after"] || err?.response?.headers?.["Retry-After"]
  const retryAfterSeconds = Number(retryAfterHeader)
  if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    return retryAfterSeconds * 1000
  }

  const detail = String(err?.response?.data?.detail || "")
  const secondsMatch = detail.match(/(\d+)\s+seconds?/i)
  if (secondsMatch) {
    return Number(secondsMatch[1]) * 1000
  }
  return DEFAULT_THROTTLE_BACKOFF_MS
}

const isThrottleError = (err: any) => err?.response?.status === 429

const isTransientPanelError = (err: any) => (
  err?.code === "ECONNABORTED" ||
  /timeout/i.test(String(err?.message || "")) ||
  String(err?.message || "") === "Network Error" ||
  !err?.response
)

export type QuestionsEmbed = { meetingId: string; panelToken: string; ownershipKey: string }
function QuestionsSession({ embed }: { embed?: QuestionsEmbed }) {
  const { meetingId: routeMeetingId } = useParams<{ meetingId: string }>()
  const [searchParams] = useSearchParams()
  const meetingId = embed?.meetingId || routeMeetingId
  const panelToken = embed?.panelToken || searchParams.get("panel_token") || ""
  const ownership = useToolOwnership(`${meetingId}:${embed?.ownershipKey ?? panelToken}`)
  const captureTool = ownership.capture
  const [role, setRole] = useState<MeetingQuestionsPanelRole>("participant")
  const [submissions, setSubmissions] = useState<MeetingQuestionSubmission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [body, setBody] = useState("")
  const [saving, setSaving] = useState(false)
  const [updatingId, setUpdatingId] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [realtimeUrl, setRealtimeUrl] = useState<string | null>(null)
  const [hostNoteDrafts, setHostNoteDrafts] = useState<Record<string, string>>({})
  const [hostNoteSaveStatuses, setHostNoteSaveStatuses] = useState<Record<string, "idle" | "saving" | "saved" | "error">>({})
  const hostNoteDraftsRef = useRef<Record<string, string>>({})
  const hostNoteTimersRef = useRef<Record<string, number>>({})
  const hasLoadedPanelRef = useRef(false)
  const throttledUntilRef = useRef(0)
  const transientRetryAfterRef = useRef(0)
  const loadPanelInFlightRef = useRef(false)
  const pendingPanelRefreshRef = useRef(false)

  const syncSubmissions = useCallback((nextSubmissions: MeetingQuestionSubmission[]) => {
    const sorted = sortQuestionSubmissions(nextSubmissions)
    setSubmissions(sorted)
    setHostNoteDrafts((prev) => {
      const next = { ...prev }
      sorted.forEach((submission) => {
        if (next[submission.id] === undefined) next[submission.id] = submission.host_note || ""
      })
      hostNoteDraftsRef.current = next
      return next
    })
  }, [])

  const loadPanel = useCallback(async (options: { silent?: boolean; force?: boolean } = {}) => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (!meetingId || !panelToken) {
      setError("Questions are unavailable.")
      setLoading(false)
      return
    }
    if (loadPanelInFlightRef.current) {
      if (options.silent) pendingPanelRefreshRef.current = true
      setLoading(false)
      return
    }
    const now = Date.now()
    if (!options.force && (now < throttledUntilRef.current || now < transientRetryAfterRef.current)) {
      setLoading(false)
      return
    }
    loadPanelInFlightRef.current = true
    try {
      const response = await meetings.getMeetingQuestionsPanel(meetingId, panelToken)
      if (!ownsTool()) return;
      setRole(response.role)
      setRealtimeUrl(response.realtime_url || null)
      syncSubmissions(response.submissions || [])
      hasLoadedPanelRef.current = true
      transientRetryAfterRef.current = 0
      setError("")
    } catch (err: any) {
      if (!ownsTool()) return;
      if (isThrottleError(err)) {
        throttledUntilRef.current = Date.now() + getThrottleBackoffMs(err)
        if (!options.silent && !hasLoadedPanelRef.current) {
          setError("Questions are temporarily paused. Please try again shortly.")
        }
        return
      }
      if (isTransientPanelError(err)) {
        transientRetryAfterRef.current = Date.now() + TRANSIENT_ERROR_BACKOFF_MS
      }
      if (!options.silent) {
        setError(err?.response?.data?.detail || err?.message || "Unable to load questions.")
      }
    } finally {
      if (!ownsTool()) return;
      loadPanelInFlightRef.current = false
      setLoading(false)
      const shouldRunPendingRefresh = pendingPanelRefreshRef.current &&
        Date.now() >= throttledUntilRef.current &&
        Date.now() >= transientRetryAfterRef.current
      pendingPanelRefreshRef.current = false
      if (shouldRunPendingRefresh) {
        window.setTimeout(() => {
          void loadPanel({ silent: true })
        }, 0)
      }
    }
  }, [meetingId, panelToken, syncSubmissions, captureTool])

  useEffect(() => {
    loadPanel()
    const intervalId = window.setInterval(() => loadPanel({ silent: true }), PANEL_POLL_INTERVAL_MS)
    return () => window.clearInterval(intervalId)
  }, [loadPanel])

  useMeetingPanelEvents({ onReconnect: () => void loadPanel({ silent: true, force: true }), panelToken,
    realtimeUrl,
    enabled: Boolean(meetingId && panelToken),
    onEvent: (event) => {
      if (event.type.startsWith("question.")) {
        loadPanel({ silent: true })
      }
    },
  })

  const refreshPanel = async () => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (refreshing || Date.now() < throttledUntilRef.current) return
    setRefreshing(true)
    transientRetryAfterRef.current = 0
    await loadPanel({ force: true })
      if (!ownsTool()) return;
    setRefreshing(false)
  }

  const submitQuestion = async () => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (!meetingId || !panelToken || saving || !body.trim()) return
    setSaving(true)
    setError("")
    try {
      const saved = await meetings.submitMeetingQuestionsPanelQuestion(meetingId, panelToken, { body: body.trim() })
      if (!ownsTool()) return;
      syncSubmissions([...submissions, saved])
      setBody("")
    } catch (err: any) {
      if (!ownsTool()) return;
      setError(err?.response?.data?.detail || err?.message || "Unable to submit your question.")
    } finally {
      if (!ownsTool()) return;
      setSaving(false)
    }
  }

  const updateSubmission = async (
    submission: MeetingQuestionSubmission,
    updates: Partial<Pick<MeetingQuestionSubmission, "status" | "host_note">>,
  ) => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    if (!meetingId || !panelToken) return
    setUpdatingId(submission.id)
    setError("")
    try {
      const saved = await meetings.updateMeetingQuestionsPanelQuestion(meetingId, panelToken, submission.id, updates)
      if (!ownsTool()) return;
      syncSubmissions(submissions.map((item) => (item.id === saved.id ? saved : item)))
    } catch (err: any) {
      if (!ownsTool()) return;
      setError(err?.response?.data?.detail || err?.message || "Unable to update this question.")
    } finally {
      if (!ownsTool()) return;
      setUpdatingId(null)
    }
  }

  const setHostNoteStatus = (submissionId: string, status: "idle" | "saving" | "saved" | "error") => {
    setHostNoteSaveStatuses((prev) => ({ ...prev, [submissionId]: status }))
  }

  const saveHostNote = async (submissionId: string, note: string) => {
    const ownsTool = captureTool(); if (!ownsTool()) return;
    const submission = submissions.find((item) => item.id === submissionId)
    if (!submission || note === (submission.host_note || "")) return
    setHostNoteStatus(submissionId, "saving")
    try {
      const saved = await meetings.updateMeetingQuestionsPanelQuestion(meetingId || "", panelToken, submissionId, { host_note: note })
      if (!ownsTool()) return;
      syncSubmissions(submissions.map((item) => (item.id === saved.id ? saved : item)))
      setHostNoteStatus(submissionId, "saved")
      window.setTimeout(() => { if (ownsTool()) setHostNoteStatus(submissionId, "idle") }, 1500)
    } catch {
      if (!ownsTool()) return;
      setHostNoteStatus(submissionId, "error")
    }
  }

  useEffect(() => () => { Object.values(hostNoteTimersRef.current).forEach(window.clearTimeout) }, [])
  const handleHostNoteChange = (submissionId: string, note: string) => {
    const ownsTool = captureTool()
    setHostNoteDrafts((prev) => {
      const next = { ...prev, [submissionId]: note }
      hostNoteDraftsRef.current = next
      return next
    })
    if (hostNoteTimersRef.current[submissionId]) window.clearTimeout(hostNoteTimersRef.current[submissionId])
    hostNoteTimersRef.current[submissionId] = window.setTimeout(() => { if (ownsTool()) void saveHostNote(submissionId, note) }, 800)
  }

  const isReviewer = role === "reviewer"

  return (
    <div
      data-testid="meeting-questions-panel"
      data-theme="daily-dark"
      className={`${embed ? "relative h-full" : "fixed inset-0 z-50"} flex min-h-0 flex-col overflow-hidden bg-[#101620] text-slate-100`}
    >
      <header className="border-b border-white/10 py-3">
        <div className="flex items-center gap-2 px-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/10 text-slate-200">
            <MessageSquare className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-sm font-semibold leading-tight text-slate-50">Questions</h1>
            <p className="mt-0.5 text-xs leading-4 text-slate-400">
            {isReviewer ? "Review submitted questions and comments." : "Submit questions for the meeting. Only meeting admins will see them."}
            </p>
          </div>
          {isReviewer && (
            <Button
              type="button"
              size="auto"
              variant="ghost"
              aria-label="Refresh questions"
              title="Refresh questions"
              onClick={refreshPanel}
              disabled={refreshing || Date.now() < throttledUntilRef.current}
              className="ml-auto h-7 min-h-7 w-7 shrink-0 rounded-md p-0 text-slate-300 hover:bg-white/10 hover:text-white disabled:text-slate-600"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
          )}
        </div>
      </header>

      <main className="flex min-h-0 flex-1 flex-col">
        {loading ? (
          <p className="py-4 text-sm text-slate-400">
            <span className="block px-3">Loading questions...</span>
          </p>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto py-3">
          {submissions.length === 0 ? (
                <p className="mx-3 rounded-md border border-white/10 bg-white/5 px-3 py-3 text-sm leading-5 text-slate-400">
              {isReviewer ? "No questions or comments yet." : "You have not submitted any questions yet."}
            </p>
          ) : (
            <div className="space-y-2 px-3">
              {submissions.map((submission) => {
                const authorName = submission.author?.name || submission.author?.email || "Unknown"
                const isUpdating = updatingId === submission.id
                return (
                  <article
                    key={submission.id}
                    data-testid={`meeting-questions-panel-submission-${submission.id}`}
                        className="rounded-md border border-white/10 bg-[#17202c] px-3 py-3 shadow-sm"
                  >
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                            <p className="truncate text-xs font-medium text-slate-200">
                          {isReviewer ? authorName : "You"}
                        </p>
                            <p className="text-[11px] leading-4 text-slate-500">{formatQuestionSubmittedAt(submission.created_at)}</p>
                      </div>
                      {isReviewer && (
                        <select
                          aria-label={`Status for ${authorName}`}
                          value={submission.status}
                          disabled={isUpdating}
                          onChange={(event) => updateSubmission(submission, { status: event.target.value as MeetingQuestionSubmissionStatus })}
                              className={`h-6 rounded-full border px-2 text-[11px] font-semibold leading-none outline-none focus:ring-2 focus:ring-sky-400/40 disabled:opacity-70 ${getQuestionStatusSelectClass(submission.status)}`}
                        >
                          <option className="bg-white text-slate-900" value="submitted">New</option>
                          <option className="bg-white text-slate-900" value="reviewed">Reviewed</option>
                          <option className="bg-white text-slate-900" value="answered">Answered</option>
                          <option className="bg-white text-slate-900" value="archived">Archived</option>
                        </select>
                      )}
                    </div>
                        <p className="whitespace-pre-line text-sm leading-5 text-slate-100">{submission.body}</p>
                    {isReviewer && (
                          <div className="mt-3 rounded-md border border-white/10 bg-black/20 px-2.5 py-2">
                            <div className="mb-1 flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                          <span>Note</span>
                          {hostNoteSaveStatuses[submission.id] === "saving" && <span>Saving...</span>}
                              {hostNoteSaveStatuses[submission.id] === "saved" && <span className="text-emerald-300">Saved</span>}
                              {hostNoteSaveStatuses[submission.id] === "error" && <span className="text-red-300">Could not save</span>}
                        </div>
                        <Textarea
                          value={hostNoteDrafts[submission.id] ?? ""}
                          onChange={(event) => handleHostNoteChange(submission.id, event.target.value)}
                          onBlur={() => saveHostNote(submission.id, hostNoteDraftsRef.current[submission.id] ?? "")}
                          placeholder="Add a host note..."
                              className="min-h-[52px] resize-none rounded-none border-0 bg-transparent p-0 text-[13px] text-slate-200 shadow-none placeholder:text-slate-600 focus-visible:ring-0"
                        />
                      </div>
                    )}
                  </article>
                )
              })}
            </div>
          )}
            </div>

            {!isReviewer && (
              <div className="border-t border-white/10 bg-[#101620]/95 px-3 py-3 backdrop-blur">
                <div className="flex items-end gap-2">
                  <Input
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault()
                        submitQuestion()
                      }
                    }}
                    placeholder="Type a question or comment..."
                    className="h-9 min-w-0 rounded-md border-white/10 bg-[#17202c] text-sm text-slate-100 placeholder:text-slate-500 focus-visible:ring-sky-400/40"
                  />
                  <Button
                    type="button"
                    size="auto"
                    onClick={submitQuestion}
                    disabled={saving || !body.trim()}
                    className="h-9 min-h-9 shrink-0 rounded-md bg-sky-500 px-3 text-xs text-white hover:bg-sky-400 disabled:bg-white/10 disabled:text-slate-500"
                  >
                    <Send className="h-3.5 w-3.5" />
                    {saving ? "Sending..." : "Send"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {error && (
        <p className="mx-3 mb-3 rounded-md border border-red-400/20 bg-red-400/10 px-3 py-2 text-xs font-medium text-red-200">
          {error}
        </p>
      )}
    </div>
  )
}

function MeetingQuestionsPanel({ embed }: { embed?: QuestionsEmbed }) {
  const { meetingId } = useParams<{ meetingId: string }>()
  const [params] = useSearchParams()
  const owner = useToolOwnership(`${meetingId}:${params.get('panel_token') || ''}`)
  return <QuestionsSession key={embed?.ownershipKey || owner.key} embed={embed} />
}
export default MeetingQuestionsPanel
